import { sharePointDriveService } from '@/services/sharepoint-drive/sharepoint-drive-service';
import { fileKeys } from '@/utils/queryKeys';
import { useQuery } from '@tanstack/vue-query';
import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import type { FileSystemItem } from '../types';

export const useFilePreviewQuery = (
  item: MaybeRefOrGetter<FileSystemItem | null>,
  isOpen: MaybeRefOrGetter<boolean>,
) => {
  const driveId = computed(() => toValue(item)?.driveId);
  const itemId = computed(() => toValue(item)?.id);

  return useQuery({
    queryKey: computed(() => fileKeys.preview(driveId.value ?? '', itemId.value ?? '')),
    queryFn: () => sharePointDriveService.getPreview(driveId.value!, itemId.value!),
    // Not withRetry-wrapped at the service, so vue-query's default retry:3 (from the app-level
    // QueryClient's lack of overrides) would be the sole retry layer here. Disable it: a preview
    // failure should surface immediately rather than after ~7s.
    retry: false,
    enabled: computed(() => {
      const current = toValue(item);
      return (
        toValue(isOpen) &&
        !!current &&
        current.type === 'file' &&
        !!driveId.value &&
        !!itemId.value
      );
    }),
    // Graph's getUrl/postUrl are short-lived signed URLs — unlike the thumbnail/details queries
    // above, this must never serve a stale cached response across re-opens of the modal.
    staleTime: 0,
    gcTime: 0,
  });
};
